import { __assign, __extends } from "tslib";
import "azure-devops-ui/CommonImports";
import "azure-devops-ui/Core/core.css";
import "./Ansi.css";
import "./LogViewer.css";
import * as Resources from '../../Resources';
import { LogParser } from '../../Utilities/LogParser';
import { getText, Parser, TimestampRegex } from '../../Utilities/Parser';
import * as React from "react";
import { ObservableValue } from "azure-devops-ui/Core/Observable";
import { Button } from "azure-devops-ui/Button";
import { Icon } from "azure-devops-ui/Icon";
import { Intersection } from "azure-devops-ui/Intersection";
import { Measure } from "azure-devops-ui/Measure";
import { Observer } from "azure-devops-ui/Observer";
import { Spinner, SpinnerSize } from "azure-devops-ui/Spinner";
import { Status, Statuses, StatusSize } from "azure-devops-ui/Status";
import { Tooltip } from "azure-devops-ui/TooltipEx";
import { FixedHeightTree } from "azure-devops-ui/TreeEx";
import { css } from "azure-devops-ui/Util";
import { TreeItemProvider } from "azure-devops-ui/Utilities/TreeItemProvider";
import { LineTransform } from "./LineTransform";
var dataLine = "data-line";
var dataSection = "data-sec";
var dataLineSection = "data-lsec";
var mainContentClass = "reader-main-content";
var Line = function (props) {
    // note: we have a test case in AnsiParser.test.ts that tests for XSS
    var copied = props.copy && props.copy.key === props.sectionKey && props.copy.line === props.line;
    var Element = props.onClick ? Button : "span";
    return (React.createElement(Element, __assign({ key: props.index, className: css("line-area flex-center flex-row flex-grow justify-start", props.className, props.onClick && "cursor-pointer"), role: "group" }, (props.onClick
        ? {
            onClick: props.onClick
        }
        : {})),
        React.createElement("span", __assign({ className: "flex-noshrink flex-self-start line", "data-lsec": props.sectionKey, "data-line": props.line, role: "treeitem" }, (props.isSnap
            ? {
                "data-snap": "1"
            }
            : {}), (props.onCopyLink
            ? {
                onClick: props.onCopyLink
            }
            : {}))),
        props.content && React.createElement("span", { className: "content" }, props.content),
        props.onCopyLink && (React.createElement(Tooltip, { text: copied ? Resources.Copied : Resources.CopyLink },
            React.createElement("span", { className: "link", "data-lsec": props.sectionKey, "data-line": props.line, onClick: props.onCopyLink },
                React.createElement(Icon, { iconName: "Link", className: "fontSizeM" }))))));
};
/**
 * Hosts log component, note that this is made to work inside a panel, some calculations might have to check to work else where
 * This virtualizes lines using the List component.
 */
var LogViewer = /** @class */ (function (_super) {
    __extends(LogViewer, _super);
    function LogViewer(props) {
        var _this = _super.call(this, props) || this;
        _this._subParser = new Parser();
        _this._container = React.createRef();
        // Needed to support sticky horizontal section header.
        _this._viewerWidth = new ObservableValue(0);
        // The largest row may be virtualized, so we need to keep track of the largest row width to set the horizontal scrollbar.
        _this._largestRowWidth = new ObservableValue(0);
        // line to scroll controlled by find
        _this._findLine = new ObservableValue(undefined);
        _this._find = new Find();
        _this._findText = "";
        _this._scrollPending = false;
        _this._autoScroll = true;
        // The maximum height of the table when virtualized. Browsers have issues
        // rendering elements that are too large and when the LogViewer contains thousands
        // of elements, the list renders very large spacer elements to correctly
        // position the scroll bar. The large spacer elements cause rendering issues
        // across browsers. To bypass this, we need to limit how large the list can
        // grow to. By default this size is 8,500,000px based of Mozilla Firefox restrictions.
        // Other browsers based on Chromium like Chrome, Opera, Edge etc can
        // render up to 30,000,000px
        _this._maxHeight = 8500000;
        // observables for sections
        _this._showLog = {};
        _this._data = {};
        _this._sections = {};
        _this._selectionChanged = {};
        _this._copyChanged = {};
        // content pending tracking for sections
        _this._pending = {};
        _this._sectionTreeRef = {};
        /**
         * Scrolls to the very top of the view, handles virtualizations so that we are at the true top
         */
        _this.toHome = function () {
            _this._noScroll();
            _this._jump(true);
        };
        /**
         * Scrolls to the very end of the view, handles virtualizations so that we are at the true end
         */
        _this.toEnd = function () {
            _this._jump();
        };
        _this._containerKeyDown = function (event) {
            // override select all to only select the text from the current focused section if exists
            if (_this._toSelectAll && (event.ctrlKey || event.metaKey) && event.key === "a") {
                var currentSection = document.querySelector("[" + dataSection + "=\"" + _this._toSelectAll + "\"]");
                if (currentSection) {
                    var selection = window.getSelection();
                    var range = document.createRange();
                    range.selectNodeContents(currentSection);
                    selection.removeAllRanges();
                    selection.addRange(range);
                    event.preventDefault();
                }
            }
        };
        _this._getTreeRef = function (sectionKey) {
            if (!_this._sectionTreeRef[sectionKey]) {
                _this._sectionTreeRef[sectionKey] = React.createRef();
            }
            return _this._sectionTreeRef[sectionKey];
        };
        _this._bindKeys = function (event) {
            var target = event.target;
            // eg: pressing Home inside search box
            if (target && target.tagName === "INPUT") {
                return;
            }
            if (event.key === "Home") {
                event.preventDefault();
                _this.toHome();
            }
            else if (event.key === "End") {
                event.preventDefault();
                _this.toEnd();
            }
        };
        _this._onMeasureContainer = function (newWidth, newHeight) {
            if (_this._viewerWidth.value !== newWidth) {
                _this._viewerWidth.value = newWidth;
                if (_this._viewerWidth.value > _this._largestRowWidth.value) {
                    _this._largestRowWidth.value = _this._viewerWidth.value;
                }
            }
        };
        _this._onSectionsChanged = function (newData) {
            (newData.removedItems || []).forEach(function (x) {
                delete _this._showLog[x.key];
                delete _this._data[x.key];
                delete _this._selectionChanged[x.key];
                delete _this._copyChanged[x.key];
                delete _this._sections[x.key];
            });
            var newItems = newData.addedItems || [];
            var updatedItems = newData.changedItems || [];
            // initialize new items and propogate updates to existing items
            _this._initializeSections(newItems.concat(updatedItems), true);
            // update observable state if there are any new items, else update to existing items will be taken care by _initializeSections
            return newItems.length > 0;
        };
        _this._findFromIndex = function (index, lines) {
            if (lines && lines.length > 0) {
                for (var i = 0; i < lines.length; i++) {
                    var lineIndex = lines[i].data.nodes[0].lineIndex;
                    if (lineIndex === index) {
                        return lines[i];
                    }
                    // the index is in the children if it is the last item or the next node is too big.
                    else if (!lines[i + 1] || lines[i + 1].data.nodes[0].lineIndex > index) {
                        return _this._findFromIndex(index, lines[i].childItems);
                    }
                }
            }
            return undefined;
        };
        _this._reFind = function () {
            if (_this._findText) {
                requestAnimationFrame(function () {
                    _this._find.find(_this._findText, _this._getFindProps());
                });
            }
        };
        _this._getFindContent = function (key) {
            if (_this._data[key]) {
                return _this._data[key].value.content;
            }
            return "";
        };
        _this._onFind = function (result) {
            if (result && result.lines.length > 0) {
                var resultLine = result.lines[result.toScrollIndex];
                var sectionData = _this._data[resultLine.key] && _this._data[resultLine.key].value;
                if (sectionData) {
                    _this._findLine.value = {
                        key: resultLine.key,
                        line: resultLine.line
                    };
                    _this._scrollPending = true;
                    sectionData.findResult = result;
                    _this._scrollToLine();
                }
            }
            else {
                _this._findLine.value = undefined;
                // get all expanded sections and update data
                Object.keys(_this._showLog).forEach(function (x) {
                    var newData = __assign({}, _this._data[x].value);
                    newData.findResult = undefined;
                    _this._data[x].value = newData;
                });
            }
        };
        _this._onScroll = function () {
            var scrollElement = _this._container.current;
            if (_this._scrollPending) {
                return;
            }
            // If the user has scrolled to the current section that is auto-scrolling, enable autoscroll.
            var lastLine = false;
            if (_this._autoScrollLine) {
                var tree = _this._getTreeRef(_this._autoScrollLine.key).current;
                var stats = tree && tree.getStats();
                lastLine = _this._autoScrollLine.line === (stats && stats.lastMaterialized + 1);
            }
            // If the user has scrolled within 25px of the end of the scroll element, trigger autoscroll. We may want to fine tune this in the future.
            _this._autoScroll =
                lastLine ||
                    (!!_this._sections && !!scrollElement && scrollElement.scrollHeight - scrollElement.offsetHeight - scrollElement.scrollTop < 25);
        };
        _this._onSnapLineClick = function (event) {
            if (event) {
                var liElement = event.currentTarget;
                if (liElement) {
                    event.stopPropagation();
                    var element = liElement.querySelector(".link");
                    if (element) {
                        var sectionKey = element.getAttribute(dataLineSection) || "";
                        _this._sections[sectionKey].value.isExpanded = false;
                        var number = parseInt(element.getAttribute(dataLine) || "");
                        _this.select(sectionKey, number);
                    }
                }
            }
        };
        _this._copyLink = function (event) {
            var _a;
            if (event) {
                var linkElement = (_a = event.target) === null || _a === void 0 ? void 0 : _a.closest('.link');
                if (linkElement) {
                    event.stopPropagation();
                    var sectionKey_1 = linkElement.getAttribute(dataLineSection) || "";
                    var number = parseInt(linkElement.getAttribute(dataLine) || "");
                    var section = _this.props.sections.value.find(function (x) { return x.key === sectionKey_1; });
                    if (section && section.getLineLink) {
                        var link_1 = section.getLineLink(section, number);
                        // copy link
                        // IE11 Compatible
                        if (window.clipboardData) {
                            window.clipboardData.setData("text", link_1);
                        }
                        var listener = function (e) {
                            if (e.clipboardData) {
                                e.clipboardData.setData("text", link_1);
                            }
                            e.preventDefault();
                        };
                        document.addEventListener("copy", listener);
                        var result = document.execCommand("copy");
                        document.removeEventListener("copy", listener);
                        if (result) {
                            _this._copy = {
                                key: sectionKey_1,
                                line: number
                            };
                            _this._copyChanged[sectionKey_1].value = !_this._copyChanged[sectionKey_1].value;
                            window.history.replaceState(window.history.state, document.title, link_1);
                        }
                    }
                }
            }
        };
        _this._parser = new LogParser();
        _this.state = {
            findActive: false
        };
        _this._initializeSections(_this.props.sections.value);
        return _this;
    }
    LogViewer.prototype.render = function () {
        var _this = this;
        return (React.createElement(Observer, { sections: { observableValue: this.props.sections, filter: this._onSectionsChanged }, find: this._find.result }, function (props) {
            var sections = props.sections;
            return (React.createElement(Intersection, null,
                React.createElement("div", { "aria-label": Resources.LogSection, className: "pl-reader fontSize " + _this.props.className, onScroll: _this._onScroll, ref: _this._container, onKeyDown: _this._containerKeyDown },
                    React.createElement(Measure, { onMeasure: _this._onMeasureContainer },
                        React.createElement("div", { className: mainContentClass + " flex-grow" },
                            React.createElement("ol", null, sections.map(function (sectionFromObservable) {
                                var sectionKey = sectionFromObservable.key;
                                return (React.createElement("li", { key: sectionKey, "data-sec": sectionKey },
                                    React.createElement(Observer, { findLine: _this._find.result, showLog: _this._showLog[sectionKey], data: _this._data[sectionKey], section: _this._sections[sectionKey], largestRowWidth: _this._largestRowWidth, viewerWidth: _this._viewerWidth }, function (sectionProps) {
                                        // constructing data that's needed to render lines
                                        var s = sectionProps.section;
                                        var lines = sectionProps.data.lines || [];
                                        var linesToRender = sectionProps.data.lines || [];
                                        var showLog = sectionProps.showLog && linesToRender.length > 0;
                                        var showSpinner = !!(_this._showLog[sectionKey] &&
                                            _this._showLog[sectionKey].value &&
                                            linesToRender.length == 0);
                                        var hasContent = s.hasData || lines.length > 0;
                                        var noStatusFaint = s.statusProps && s.statusProps === Statuses.Failed;
                                        // constructing data that's needed to parse find results
                                        var parsedFindResult = undefined;
                                        var dataFindResult = sectionProps.findLine;
                                        if (dataFindResult) {
                                            var marker = dataFindResult.markers[sectionKey];
                                            if (marker) {
                                                parsedFindResult = __assign({}, dataFindResult);
                                                // send slice of lines that belong to this section
                                                parsedFindResult.lines = dataFindResult.lines;
                                                // send selected line
                                                var selectedLine = dataFindResult.lines[dataFindResult.toScrollIndex];
                                                if (selectedLine && selectedLine.key === sectionKey) {
                                                    parsedFindResult.selectedLine = selectedLine.line;
                                                }
                                            }
                                        }
                                        var controlId = sectionKey + "-log";
                                        var rowWidth = sectionProps.largestRowWidth <= sectionProps.viewerWidth
                                            ? "100%"
                                            : sectionProps.largestRowWidth + "px";
                                        // we can't use flex here since we need position:sticky to work, so we calculate the width of the section header.
                                        var buttonWidth = sectionProps.viewerWidth -
                                            (24 + 1) * 2; /* 24 padding - 1px border on each side */
                                        return (React.createElement("div", { className: css("flex-column content-area", s.getLineLink && "linkable", s.className), style: { width: rowWidth } },
                                            React.createElement("div", { className: "flex-row sec-sticky" },
                                                React.createElement(Button, __assign({ className: css("flex flex-row sec-area", hasContent && "cursor-pointer", showLog && "expand", s.hidden && "hide"), ariaControls: controlId, disabled: !hasContent, ariaExpanded: showLog }, (hasContent
                                                    ? { onClick: function (e) { return _this._onToggleLog(sectionKey); } }
                                                    : {})),
                                                    React.createElement("div", { className: "status-area flex flex-grow", style: { width: buttonWidth + "px" } },
                                                        React.createElement("div", { className: "flex-grow flex-self-center flex flex-row sec-content" },
                                                            React.createElement(SecStatus, { iconProps: s.iconProps, statusProps: s.statusProps, spinner: showSpinner, noStatusFaint: noStatusFaint }),
                                                            React.createElement("span", { className: "title text-ellipsis body-m" }, s.title)),
                                                        s.getFarRight && (React.createElement("div", { className: "far-right body-s flex-self-center" }, s.getFarRight(s))))),
                                                React.createElement("div", { className: "flex-row flex-grow" })),
                                            showSpinner ? (React.createElement(Spinner, { size: SpinnerSize.large, label: Resources.SpinnerLabel })) : (React.createElement(FixedHeightTree, { id: controlId, className: css(!showLog && "hide"), focuszoneProps: {
                                                    activateOnEnter: !!s.getLineLink
                                                }, itemProvider: showLog
                                                    ? sectionProps.data.treeProvider
                                                    : new TreeItemProvider([]), maxHeight: _this._maxHeight, onToggle: function (event, treeItem) {
                                                    if (treeItem.underlyingItem.childItems &&
                                                        sectionProps.data.treeProvider &&
                                                        !event.defaultPrevented) {
                                                        sectionProps.data.treeProvider.toggle(treeItem.underlyingItem);
                                                        event.preventDefault();
                                                    }
                                                }, pageSize: 60, renderLoadingRow: function (rowIndex, rowDetails) {
                                                    var asyncInfo = sectionProps.section.asyncInfo;
                                                    if (asyncInfo) {
                                                        asyncInfo.getMore(asyncInfo.payload);
                                                        return (React.createElement("div", { className: "flex-row flex-grow" },
                                                            React.createElement("div", { className: "shimmer shimmer-line log-async-loading" })));
                                                    }
                                                    return React.createElement("div", null);
                                                }, renderRow: function (index, item, details) {
                                                    return (React.createElement(Observer, { findResult: _this._find.result, x: _this._copyChanged[sectionKey] }, function (rowProps) {
                                                        var rowIndex = index + 1;
                                                        var isSelected = _this._selection &&
                                                            _this._selection.key === sectionKey;
                                                        var isLineSelected = isSelected &&
                                                            _this._selection.lineToScroll ===
                                                                rowIndex;
                                                        var isCopiedLine = _this._copy && _this._copy.line === rowIndex;
                                                        var extraClassName = isLineSelected || isCopiedLine ? "highlight" : "";
                                                        var dataFindResult = rowProps.findResult;
                                                        var lineParsedResult = undefined;
                                                        if (dataFindResult) {
                                                            var marker = dataFindResult.markers[sectionKey];
                                                            if (marker) {
                                                                lineParsedResult = __assign({}, dataFindResult);
                                                                // send slice of lines that belong to this section
                                                                lineParsedResult.lines = dataFindResult.lines;
                                                                // send selected line
                                                                var selectedLine = dataFindResult.lines[dataFindResult.toScrollIndex];
                                                                if (selectedLine &&
                                                                    selectedLine.key === sectionKey) {
                                                                    lineParsedResult.selectedLine =
                                                                        selectedLine.line;
                                                                }
                                                            }
                                                        }
                                                        var transformedLine = (React.createElement(LineTransform, { expanded: item.underlyingItem.expanded, section: sectionKey, line: item.underlyingItem.data, pureHTML: s.showHtml, subParser: _this._subParser, raw: sectionProps.data.content, lineNumber: rowIndex, findResult: lineParsedResult, contentId: "rowContent-" + index }));
                                                        return (React.createElement("div", { className: css("line-row flex-row flex-grow", item.underlyingItem.childItems &&
                                                                "group-item") },
                                                            React.createElement(Line, { index: rowIndex, sectionKey: sectionKey, className: extraClassName, line: rowIndex, content: transformedLine, onCopyLink: s.getLineLink && _this._copyLink, copy: _this._copy })));
                                                    }));
                                                }, rowHeight: 20, ref: sectionProps.data.treeRef })),
                                            !showLog && s.snaps && s.snaps.length > 0 && (React.createElement("div", { className: "lines-content" }, s.snaps.map(function (snap, index) {
                                                var lines = _this._parser.parse(snap.content);
                                                // snap content is supposed to have only one line
                                                var lineData = lines[0];
                                                var transformed = (React.createElement(LineTransform, { section: sectionKey, line: lineData, subParser: _this._subParser, raw: snap.content, lineNumber: snap.line, findResult: parsedFindResult }));
                                                return (transformed && (React.createElement(Line, { index: index, sectionKey: sectionKey, key: sectionKey + index, className: "snap", isSnap: true, line: snap.line, content: transformed, onClick: _this._onSnapLineClick, onCopyLink: s.getLineLink && _this._copyLink, copy: _this._copy })));
                                            })))));
                                    })));
                            })))))));
        }));
    };
    LogViewer.prototype.componentDidMount = function () {
        var sectionToGet = this._selection && this._selection.key;
        if (sectionToGet) {
            this._setContent(sectionToGet);
        }
        if (this._container.current) {
            this._container.current.focus();
        }
        this._find.result.subscribe(this._onFind);
    };
    LogViewer.prototype.componentWillUnmount = function () {
        this._unregisterScrollable();
        this._find.result.unsubscribe(this._onFind);
    };
    /**
     * This can handle situations that could break user expectations with scrolling
     * Eg: Pressing "End" should go the true end
     * @param element Elements that's scrollable
     */
    LogViewer.prototype.initializeScrollable = function (element) {
        this._scrollable = element;
        this._scrollable.addEventListener("keydown", this._bindKeys);
    };
    /**
     * Selects a section/line
     * @param key section to select
     * @param lineNumber linenumber to scroll to in the specified section
     */
    LogViewer.prototype.select = function (key, lineNumber) {
        if (!this._showLog[key]) {
            // bad selection
            return;
        }
        if (lineNumber) {
            this._selection = {
                key: key,
                lineToScroll: lineNumber
            };
            this._toLine = {
                key: key,
                line: lineNumber
            };
            this._scrollPending = true;
        }
        else {
            // if linenumber is not given, "unselect" the existing line selection if it exists
            this._selection = undefined;
        }
        // After we initialize lines to scroll as needed, make sure that we show this section, note that we don't collapse once we expanded, so there's no way to "unselect" an expanded section/task
        this._setContent(key);
        // notify observer to re-render that section
        this._selectionChanged[key].value = !this._selectionChanged[key].value;
        this._toSelectAll = key;
    };
    /**
     * Updates the content in the section by force calling getData() to get new content and auto expanding the section if the user didn't explictly interacted with the section
     * If this section already sent hasContent as well, there's no need for the section to update that, we handle the case where we have data even if hasContent is false
     * @param key section to update content for
     */
    LogViewer.prototype.updateContent = function (key) {
        // updates the content for section at key only if there's no existing state/ user didn't interact with it yet
        var existingValue = this._showLog[key] && this._showLog[key].value;
        if (existingValue === undefined || existingValue === true) {
            this._setContent(key, true);
        }
    };
    /**
     * Brings focus to the reader
     */
    LogViewer.prototype.focus = function () {
        this._container.current && this._container.current.focus();
    };
    /**
     * Finds the supplied text with in expanded content
     */
    LogViewer.prototype.find = function (text) {
        this._noScroll();
        this._findText = text;
        this._find.find(this._findText, this._getFindProps());
    };
    /**
     * When find is active, moves to the next match
     */
    LogViewer.prototype.findNext = function () {
        if (this._findText) {
            this._find.down();
        }
    };
    /**
     * Clears find
     */
    LogViewer.prototype.clearFind = function () {
        if (this._find.result) {
            this._find.result.value = undefined;
        }
    };
    /**
     * When find is active, moves to the previous match
     */
    LogViewer.prototype.findPrev = function () {
        if (this._findText) {
            this._find.up();
        }
    };
    /**
     * Gets find result observable, consumers can subscribe to this to listen to result changes
     */
    LogViewer.prototype.getFindResult = function () {
        return this._find.result;
    };
    LogViewer.prototype._noScroll = function () {
        this._autoScroll = false;
        delete this._autoScrollLine;
    };
    LogViewer.prototype._getTreeItemsFromLines = function (lines) {
        var group = {};
        var groupIndex = -1;
        var treeItems = [];
        for (var i = 0; i < lines.length; i++) {
            var line = lines[i];
            var node = line.nodes[0];
            if (line.nodes.some(function (n) { return !!n.isGroup; })) {
                if (groupIndex != -1) {
                    // add previous group
                    treeItems.push(group[groupIndex]);
                    groupIndex = -1;
                }
                groupIndex = line.nodes[0].lineIndex;
                group[groupIndex] = { data: line };
            }
            else if (node.group && group[node.group.lineIndex]) {
                var groupTreeItem = group[node.group.lineIndex];
                if (groupTreeItem.childItems) {
                    groupTreeItem.childItems.push({ data: line });
                }
                else {
                    groupTreeItem.childItems = [{ data: line }];
                }
            }
            else {
                if (groupIndex != -1) {
                    // add previous group
                    treeItems.push(group[groupIndex]);
                    groupIndex = -1;
                }
                treeItems.push({ data: line });
            }
        }
        if (groupIndex != -1) {
            treeItems.push(group[groupIndex]);
        }
        return treeItems;
    };
    LogViewer.prototype._jump = function (top) {
        if (top === void 0) { top = false; }
        var allSections = this.props.sections.value || [];
        var section = top ? allSections[0] : allSections[allSections.length - 1];
        this._goTo(section, top);
    };
    /**
     * Scrolls to the section specified, handles virtualization
     * @param section section to focus on
     * @param top whether to focus on the top or bottom of the section if there's only one
     */
    LogViewer.prototype._goTo = function (section, top) {
        if (this._container.current && section) {
            // ignore any intersections
            var element = this._container.current.querySelector("[" + dataSection + "=\"" + section.key + "\"]");
            var allSections = this.props.sections.value || [];
            if (element) {
                // scroll to section if there are more than 1
                allSections.length > 1 &&
                    element.scrollIntoView({
                        behavior: "auto",
                        block: "center"
                    });
                allSections.length == 1 && element.scrollIntoView(top);
            }
        }
    };
    LogViewer.prototype._unregisterScrollable = function () {
        if (this._scrollable) {
            this._scrollable.removeEventListener("keydown", this._bindKeys);
        }
    };
    LogViewer.prototype._initializeSections = function (sections, isUpdate) {
        var _this = this;
        if (isUpdate === void 0) { isUpdate = false; }
        var selectedSection = this._selection ? this._selection.key : null;
        sections.forEach(function (s) {
            if (_this._sections[s.key]) {
                // propogate updates for existing item
                _this._sections[s.key].value = s;
                if (isUpdate) {
                    // force update content if this is already visible
                    if (_this._showLog[s.key].value) {
                        _this._setContent(s.key, true);
                    }
                    return;
                }
            }
            else {
                _this._sections[s.key] = new ObservableValue(s);
            }
            var value = selectedSection === s.key || s.isExpanded ? true : undefined;
            if (_this._showLog[s.key]) {
                _this._showLog[s.key].value = value;
            }
            else {
                _this._showLog[s.key] = new ObservableValue(value);
            }
            if (_this._data[s.key]) {
                _this._data[s.key].value = {};
            }
            else {
                _this._data[s.key] = new ObservableValue({});
            }
            if (_this._selectionChanged[s.key]) {
                _this._selectionChanged[s.key].value = false;
            }
            else {
                _this._selectionChanged[s.key] = new ObservableValue(false);
            }
            if (_this._copyChanged[s.key]) {
                _this._copyChanged[s.key].value = false;
            }
            else {
                _this._copyChanged[s.key] = new ObservableValue(false);
            }
            if (s.isExpanded) {
                _this._setContent(s.key);
            }
        });
    };
    LogViewer.prototype._scroll = function (section, lineNumber, placement, done) {
        if (placement === void 0) { placement = "center"; }
        var index = lineNumber - 1;
        if (section && section.treeItems && section.treeRef && section.treeRef.current) {
            var treeItem = this._findFromIndex(index, section.treeItems);
            treeItem && section.treeProvider && section.treeProvider.expand(treeItem, true);
            section.treeRef.current.scrollIntoView(index, { block: placement });
        }
        // Run done() after the next animation frame so onScroll runs before we clean up.
        requestAnimationFrame(function () {
            done && done();
        });
    };
    LogViewer.prototype._recountLineIndexIfHasGroups = function (index, content) {
        var arrayOfStrings = content.split("\r\n");
        for (var i = 0; i < arrayOfStrings.length && index > i; i++) {
            if (arrayOfStrings[i] == "##[endgroup]") {
                index--;
            }
        }
        return index;
    };
    LogViewer.prototype._getFindProps = function () {
        var _this = this;
        var lines = [];
        var sectionMarkers = [];
        var sectionLimits = [];
        var sectionKeys = [];
        Object.keys(this._sections).forEach(function (sKey) {
            // get only expanded sections information
            var sectionData = _this._showLog[sKey] && _this._showLog[sKey].value && _this._data[sKey] && _this._data[sKey].value;
            if (sectionData) {
                sectionKeys.push(sKey);
                var listStats = sectionData.treeRef && sectionData.treeRef.current && sectionData.treeRef.current.getStats();
                listStats &&
                    sectionLimits.push({
                        startIndex: listStats.firstMaterialized,
                        endIndex: listStats.lastMaterialized
                    });
                var markerStart = Math.max(lines.length - 1, 0);
                lines = lines.concat(sectionData.lines);
                var markerEnd = Math.max(lines.length - 1, 0);
                sectionMarkers.push({
                    startIndex: markerStart,
                    endIndex: markerEnd
                });
            }
        });
        return {
            getContent: this._getFindContent,
            lines: lines,
            sectionKeys: sectionKeys,
            sectionMarkers: sectionMarkers,
            sectionLimits: sectionLimits
        };
    };
    LogViewer.prototype._onToggleLog = function (key) {
        var _this = this;
        this._showLog[key].value = !this._showLog[key].value;
        if (this._showLog[key].value) {
            this._setContent(key);
            requestAnimationFrame(function () {
                _this._scroll(_this._data[key].value, 0, "nearest");
            });
        }
        var largestWidth = this._viewerWidth.value - 20;
        this.props.sections.value.forEach(function (section) {
            var sectionData = _this._data[section.key].value;
            if (_this._showLog[section.key].value && !!sectionData.largestRowWidth)
                largestWidth = Math.max(largestWidth, sectionData.largestRowWidth);
        });
        this._largestRowWidth.value = largestWidth;
        this._reFind();
    };
    /**
     * Makes sure that section is expanded and fetches the content based on props, once fetched, this makes no attempt to get new content again unless 'force' is sent
     * @param key section to expand/show content
     * @param force If true, fetches content from props everytime, defaults to false
     */
    LogViewer.prototype._setContent = function (key, force) {
        var _this = this;
        if (force === void 0) { force = false; }
        var section = this.props.sections.value.find(function (x) { return x.key === key; });
        if (section) {
            if (!this._showLog[key].value) {
                this._showLog[key].value = true;
            }
            var sectionData = this._data[key] && this._data[key].value;
            if (force || !sectionData.content) {
                this._pending[section.key] = true;
                var data = section.getData();
                if (data) {
                    if (data.then) {
                        data.then(function (data) {
                            _this._onData(section, key, data, force);
                        });
                    }
                    else {
                        this._onData(section, key, data, force);
                    }
                }
            }
            else {
                this._onData(section, key, sectionData.content);
            }
        }
    };
    LogViewer.prototype._onData = function (section, key, data, force) {
        if (!this._data[key]) {
            console.error("section data can't be null", key);
            return;
        }
        var oldData = this._data[key].value;
        var oldLinesLength = 0;
        var content = data || "";
        var parser = section.parser || this._parser;
        var parseAllLines = oldData.showTimestamps != this.props.showTimestamps;
        if (!parseAllLines && content.indexOf(oldData.content) > 0) {
            parseAllLines = true;
        }
        // replace any timestamps in the beginning of the logs
        content = this.props.showTimestamps ? content : content.replace(TimestampRegex, "");
        // curled double quotes are left after echo command, so it lead to errors of parsing
        content = content.replace(/[“”]/g, '');
        var lines;
        if (!parseAllLines) {
            var startParseIndex = oldData.content ? oldData.content.length : 0;
            oldLinesLength = oldData.lines ? oldData.lines.length : 0;
            lines = parser.parse(content, startParseIndex, oldLinesLength);
        }
        else {
            lines = parser.parse(content);
        }
        var largestRowWidth = 0;
        lines.forEach(function (line) {
            var nodesCount = line.nodes.length;
            if (nodesCount > 0) {
                var start = line.nodes[0].start;
                var end = line.nodes[nodesCount - 1].end;
                largestRowWidth = Math.max(largestRowWidth, (end - start) * 8 /* 8 is an estimate px width per character */);
            }
        });
        var newTreeItems = this._getTreeItemsFromLines(lines);
        var treeItems = oldData.treeItems && !parseAllLines ? oldData.treeItems.concat(newTreeItems) : newTreeItems;
        var treeItemProvider = new TreeItemProvider(treeItems);
        var asyncInfo = section.asyncInfo;
        if (asyncInfo) {
            if (asyncInfo.hasMore) {
                // if we have more data, inject the shimmer(by making an item as undefined) to the top of the log
                treeItemProvider.value.unshift({
                    underlyingItem: {
                        data: undefined
                    }
                });
            }
        }
        // adjust lize size to fill the view
        var treeRef = this._getTreeRef(key);
        if (this._largestRowWidth.value < largestRowWidth) {
            this._largestRowWidth.value = largestRowWidth;
        }
        var newData = {
            lines: oldLinesLength ? oldData.lines.concat(lines) : lines,
            content: content,
            findResult: oldData.findResult,
            treeProvider: treeItemProvider,
            treeItems: treeItems,
            treeRef: treeRef,
            largestRowWidth: largestRowWidth,
            showTimestamps: !!this.props.showTimestamps
        };
        // update observable
        this._data[key].value = newData;
        !force && this._reFind();
        delete this._pending[section.key];
        if (force && !section.noAutoScroll && this._autoScroll && oldData && oldData.lines && oldData.lines.length < newData.lines.length) {
            this._autoScrollLine = {
                key: key,
                line: newData.lines.length
            };
            this._scrollPending = true;
        }
        else {
            delete this._autoScrollLine;
        }
        this._scrollToLine();
    };
    LogViewer.prototype._deferScroll = function () {
        var _this = this;
        // This is not a hack, by pushing the scroll to end, we are making sure that the render loop from observable completes and the element is available for us to scroll happily
        requestAnimationFrame(function () {
            _this._scrollToLine();
        });
    };
    LogViewer.prototype._scrollToLine = function () {
        var _this = this;
        var lineToScroll = this._toLine || this._findLine.value || this._autoScrollLine;
        if (!lineToScroll || this._pending[lineToScroll.key]) {
            // If the content is still pending no-op
            return;
        }
        var sectionData = lineToScroll && this._data[lineToScroll.key] && this._data[lineToScroll.key].value;
        if (this._scrollPending && lineToScroll && sectionData && sectionData.lines && this._container.current && sectionData.lines.length > 0) {
            // get our section element first
            var section = this._container.current.querySelector("[" + dataSection + "=\"" + lineToScroll.key + "\"]");
            if (section) {
                var isSnap = section.querySelector('[data-snap="1"]');
                if (isSnap) {
                    // if this is a snap element, defer
                    this._deferScroll();
                    return;
                }
                var cleanUp = function () {
                    _this._scrollPending = false;
                    // clean up the temporary lines to scroll
                    if (lineToScroll === _this._toLine) {
                        _this._toLine = undefined;
                    }
                    if (lineToScroll === _this._findLine.value) {
                        _this._findLine.value = undefined;
                    }
                };
                var placement = "nearest";
                if (lineToScroll === this._toLine || lineToScroll === this._findLine.value) {
                    // when asked to scroll to a particular line explictly (through URL), place it in center
                    placement = "center";
                }
                if (lineToScroll === this._autoScrollLine) {
                    placement = "end";
                }
                lineToScroll.line = this._recountLineIndexIfHasGroups(lineToScroll.line, sectionData.content);
                sectionData && this._scroll(sectionData, lineToScroll.line, placement, cleanUp);
            }
        }
    };
    return LogViewer;
}(React.Component));
export { LogViewer };
function SecStatus(props) {
    var _a = React.useState(false), spin = _a[0], setSpin = _a[1];
    React.useLayoutEffect(function () {
        if (props.spinner) {
            var timerId_1 = setTimeout(function () {
                props.spinner && setSpin(true);
            }, 150);
            return function () {
                clearTimeout(timerId_1);
            };
        }
        else {
            setSpin(false);
        }
    }, [props.spinner]);
    return (React.createElement(React.Fragment, null,
        spin && React.createElement(Spinner, { size: SpinnerSize.xSmall, className: "status-spinner" }),
        !spin && (React.createElement(React.Fragment, null,
            props.statusProps && (React.createElement(Status, __assign({}, props.statusProps, { className: "flex-self-center fontSizeM " + (props.noStatusFaint ? "" : "faint"), size: StatusSize.m }))),
            props.iconProps && React.createElement(Icon, __assign({}, props.iconProps, { className: "fontSizeML status-icon" }))))));
}
var Find = /** @class */ (function () {
    function Find() {
        var _this = this;
        this._toScrollIndex = 0;
        this._value = "";
        this._result = new ObservableValue(undefined);
        this._onChange = function (event, value, props) {
            _this._value = value;
            // we need to reset the index to scroll if the search text or sections to search in are changed
            if (_this._value !== _this._prevValue || props.sectionKeys !== _this._prevSearchSectionKeys) {
                _this._toScrollIndex = 0;
            }
            _this._prevValue = value;
            _this._prevSearchSectionKeys = props.sectionKeys;
            var result;
            var findResultLines = [];
            var markers = {};
            if (value) {
                var lines = props.lines;
                // find the actual starting point
                for (var markerIndex = 0; markerIndex < props.sectionMarkers.length; markerIndex++) {
                    var markerStart = Math.max(findResultLines.length - 1, -1);
                    var sectionKey = props.sectionKeys[markerIndex];
                    // lines: ...|sectionStart.....pageStart......sectionEnd|...
                    var sectionStart = props.sectionMarkers[markerIndex].startIndex;
                    var pageStart = Math.max(props.sectionLimits[markerIndex].startIndex, 0);
                    var sectionEnd = props.sectionMarkers[markerIndex].endIndex;
                    // we should jump start to the line for this section, to start our search from the line user sees
                    // lines: ...|sectionStart.....[pageStart......sectionEnd]|...
                    // pageStart to end loop
                    for (var index = sectionStart + pageStart; index <= sectionEnd; index++) {
                        var line = lines[index];
                        var lineNumber = markerIndex > 0 ? Math.max(index - sectionStart, 0) : index + 1;
                        _this._match(value, sectionKey, line, lineNumber, findResultLines, props);
                    }
                    // lines: ...|[sectionStart.....]pageStart......sectionEnd|...
                    // start to pageStart loop
                    if (pageStart !== sectionStart) {
                        for (var index = sectionStart; index <= pageStart; index++) {
                            var line = lines[index];
                            var lineNumber = markerIndex > 0 ? Math.max(index - sectionStart, 0) : index + 1;
                            _this._match(value, sectionKey, line, lineNumber, findResultLines, props);
                        }
                    }
                    var markerEnd = Math.max(findResultLines.length - 1, 0);
                    if (findResultLines.length > 0) {
                        markers[sectionKey] = {
                            startIndex: markerStart + 1,
                            endIndex: markerEnd
                        };
                    }
                }
            }
            if (findResultLines.length > 0) {
                result = {
                    lines: findResultLines,
                    text: value,
                    toScrollIndex: _this._toScrollIndex,
                    scrollCount: findResultLines.length,
                    markers: markers
                };
            }
            _this._result.value = result;
        };
    }
    Find.prototype.find = function (text, props) {
        this._onChange(null, text, props);
    };
    Object.defineProperty(Find.prototype, "result", {
        get: function () {
            return this._result;
        },
        enumerable: false,
        configurable: true
    });
    Find.prototype.down = function () {
        if (this._result.value) {
            var toChange = this._toScrollIndex + 1;
            this._toScrollIndex = toChange > this._result.value.lines.length - 1 ? 0 : toChange;
            var newResult = __assign({}, this._result.value);
            newResult.toScrollIndex = this._toScrollIndex;
            this._result.value = newResult;
        }
    };
    Find.prototype.up = function () {
        if (this._result.value) {
            var toChange = this._toScrollIndex - 1;
            this._toScrollIndex = toChange < 0 ? this._result.value.lines.length - 1 : toChange;
            var newResult = __assign({}, this._result.value);
            newResult.toScrollIndex = this._toScrollIndex;
            this._result.value = newResult;
        }
    };
    Find.prototype._match = function (value, sectionKey, line, lineNumber, result, props) {
        var foundMatch = false;
        var nodesCount = line.nodes.length;
        if (nodesCount > 0) {
            var start = line.nodes[0].start;
            var end = line.nodes[nodesCount - 1].end;
            var content = props.getContent(sectionKey);
            var data = getText(content.substring(start, end));
            if (data.indexOf(getText(value)) !== -1) {
                foundMatch = true;
            }
        }
        if (foundMatch) {
            result.push({
                key: sectionKey,
                line: lineNumber
            });
        }
    };
    return Find;
}());
/* tslint:enable*/ 
